import { forwardRef, useMemo } from 'react'
import type { ChangeEvent, FormEvent, InputHTMLAttributes, CSSProperties } from 'react'

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'value' | 'min' | 'max' | 'step'> {
  min?: number
  max?: number
  step?: number
  value: number
  onChange?: (next: number) => void
  loading?: boolean
  wrapperClassName?: string
  wrapperStyle?: CSSProperties
}

export const Slider = forwardRef<HTMLInputElement, Props>(function Slider(
  { min = 0, max = 100, step = 1, value, onChange, loading = false, disabled, className, wrapperClassName, wrapperStyle, ...rest },
  ref,
) {
  const pct = useMemo(() => {
    if (max <= min) return 0
    return ((Math.min(Math.max(value, min), max) - min) / (max - min)) * 100
  }, [value, min, max])

  const classes = [
    'dsSl',
    disabled || loading ? 'is-disabled' : '',
    loading ? 'is-busy' : '',
    wrapperClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const style: CSSProperties = { ['--val' as string]: `${pct}%`, ...(wrapperStyle ?? {}) }
  const handleChange = (event: ChangeEvent<HTMLInputElement> | FormEvent<HTMLInputElement>) => {
    onChange?.(Number(event.currentTarget.value))
  }

  return (
    <div className={classes} style={style}>
      <input
        ref={ref}
        type="range"
        className={`dsSl__input ${className ?? ''}`.trim()}
        {...rest}
        min={min}
        max={max}
        step={step}
        value={value}
        onInput={handleChange}
        onChange={handleChange}
        disabled={disabled || loading}
      />
      <div className="dsSl__track">
        <span className="dsSl__fill" />
        <span className="dsSl__thumb" />
      </div>
    </div>
  )
})
